/**
 * In-memory image cache — Map<fileId, objectURL>.
 *
 * Prevents re-downloading images that were just sent or already viewed.
 * Uses LRU eviction when cache exceeds MAX_ENTRIES.
 */

const MAX_ENTRIES = 200;

class ImageCache {
  constructor() {
    /** @type {Map<string, string>} fileId → objectURL */
    this._map = new Map();
  }

  /**
   * Get cached object URL for a file.
   * Moves the entry to the end (most recently used).
   * @param {string|number} fileId
   * @returns {string|null} objectURL or null
   */
  get(fileId) {
    const key = String(fileId);
    const url = this._map.get(key);
    if (!url) return null;

    // LRU: move to end
    this._map.delete(key);
    this._map.set(key, url);
    return url;
  }

  /**
   * Cache an object URL for a file.
   * @param {string|number} fileId
   * @param {string} objectURL
   */
  set(fileId, objectURL) {
    const key = String(fileId);

    // If already cached, just update
    if (this._map.has(key)) {
      this._map.delete(key);
    }

    this._map.set(key, objectURL);

    // Evict oldest entries if over limit
    while (this._map.size > MAX_ENTRIES) {
      const oldest = this._map.keys().next().value;
      const oldURL = this._map.get(oldest);
      this._map.delete(oldest);
      // Revoke to free memory
      try { URL.revokeObjectURL(oldURL); } catch (_) {}
    }
  }

  /**
   * Check if file is cached.
   * @param {string|number} fileId
   * @returns {boolean}
   */
  has(fileId) {
    return this._map.has(String(fileId));
  }
}

export const imageCache = new ImageCache();
